import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SearchScope, getSearchEntityType } from './search.helpers';

export interface SearchResult {
  id: string;
  name?: string;
  title?: string;
  type?: string;
  entityType?: string;
  sourceType?: string;
  source?: string;
  sourceLabel?: string;
  module?: string;
  route?: string;
  routeType?: string;
  publicRoute?: string;
  adminRoute?: string;
  locationType?: string;
  companyId?: string;
  companyName?: string;
  companyLogoUrl?: string;
  logoUrl?: string;
  logo?: string;
  imageUrl?: string;
  industryType?: string;
  jobTitle?: string;
  jobPosition?: string;
  positionTitle?: string;
  department?: string;
  category?: string;
  company?: string;
  confidential?: boolean;
  description?: string;
  jobDescription?: string;
}

export interface SearchResponse {
  content: SearchResult[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  search(
    keyword: string,
    page: number = 0,
    size: number = 10,
    scope: SearchScope = 'public'
  ): Observable<SearchResponse> {
    // The backend exposes only the public search endpoint, so all scopes use it.
    return this.searchPublic(keyword, page, size);
  }

  /**
   * Search for jobs/positions using keyword
   * @param keyword Search keyword (min 2 chars)
   * @param page Page number (default 0)
   * @param size Page size (default 10)
   */
  searchPublic(keyword: string, page: number = 0, size: number = 10): Observable<SearchResponse> {
    if (keyword.trim().length < 2) {
      throw new Error('Keyword must be at least 2 characters');
    }

    return this.http.get<SearchResponse>(
      `${this.apiUrl}api/search/public`,
      {
        params: {
          keyword: keyword.trim(),
          page: page.toString(),
          size: size.toString()
        }
      }
    );
  }

  searchAdmin(keyword: string, page: number = 0, size: number = 10, type?: string): Observable<SearchResponse> {
    // Kept for backward compatibility with older callers.
    return this.searchPublic(keyword, page, size);
  }

  normalizeResponse(response: SearchResponse | SearchResult[] | any): SearchResponse {
    const content = Array.isArray(response)
      ? response
      : (response?.content ?? []);

    return {
      content: content.map((item: any) => this.normalizeResult(item)),
      totalElements: response?.totalElements ?? content.length ?? 0,
      totalPages: response?.totalPages ?? 1,
      currentPage: response?.currentPage ?? response?.number ?? 0,
    };
  }

  normalizeResult(result: any): SearchResult {
    const entityType = getSearchEntityType(result);
    const companyName = result?.companyName ?? result?.company ?? '';
    const title =
      result?.title ??
      result?.jobTitle ??
      result?.positionTitle ??
      result?.jobPosition ??
      result?.name ??
      '';

    return {
      ...result,
      id: String(result?.id ?? ''),
      type: result?.type ?? entityType,
      entityType: result?.entityType ?? entityType,
      companyName,
      company: result?.company ?? companyName,
      companyLogoUrl: result?.companyLogoUrl ?? result?.logoUrl ?? result?.logo ?? '',
      logoUrl: result?.logoUrl ?? result?.companyLogoUrl ?? '',
      logo: result?.logo ?? result?.companyLogoUrl ?? '',
      imageUrl: result?.imageUrl ?? '',
      industryType: result?.industryType ?? '',
      title,
      jobTitle: result?.jobTitle ?? title,
      jobPosition: result?.jobPosition ?? result?.positionTitle ?? title,
      description: result?.description ?? result?.jobDescription ?? '',
      jobDescription: result?.jobDescription ?? result?.description ?? '',
      confidential: !!result?.confidential,
    };
  }
}
